import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ExecutiveMember {
  name: string;
  title: string;
  image: string;
  alt: string;
  credentials: string[];
  expertise: string;
}

interface ExecutiveTeamSectionProps {
  className?: string;
}

const ExecutiveTeamSection = ({ className = '' }: ExecutiveTeamSectionProps) => {
  const executives: ExecutiveMember[] = [
  {
    name: "Daniel Chen",
    title: "Founder & Chief Infrastructure Officer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d805f55-1763295574780.png",
    alt: "Asian man in navy suit with glasses standing in modern office with city skyline background",
    credentials: [
    "15+ years in ad-tech infrastructure",
    "Former platform compliance director at major tech company",
    "Hong Kong regulatory framework specialist"],

    expertise: "Platform policy interpretation, regulatory compliance architecture, enterprise infrastructure design"
  },
  {
    name: "Sarah Mitchell",
    title: "Chief Compliance Officer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17ad1303d-1767329366076.png",
    alt: "Professional woman with blonde hair in gray blazer sitting at desk with legal documents",
    credentials: [
    "12+ years in advertising compliance",
    "Certified in international advertising regulations",
    "Specialist in high-risk vertical compliance"],

    expertise: "Multi-jurisdictional compliance, platform policy alignment, risk assessment frameworks"
  },
  {
    name: "Marcus Thompson",
    title: "Chief Technology Officer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19895ccd6-1763296676010.png",
    alt: "Man with beard wearing black shirt working on laptop in modern tech office",
    credentials: [
    "20+ years in enterprise infrastructure",
    "Former engineering lead at financial services firm",
    "Specialist in white-label platform architecture"],

    expertise: "Scalable infrastructure design, API integration, enterprise security protocols"
  }];


  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="max-w-3xl mb-16">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-6">
            Leadership Team
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Our executive team brings decades of combined experience in advertising infrastructure, regulatory compliance, and enterprise technology. We understand the intersection of ad-tech and institutional operations because we've built it from the inside.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {executives.map((executive, index) =>
          <div
            key={index}
            className="bg-surface rounded-sm border border-border overflow-hidden hover:border-accent transition-colors duration-300">

              <div className="relative h-80 overflow-hidden">
                <AppImage
                src={executive.image}
                alt={executive.alt}
                className="w-full h-full object-cover" />

              </div>
              
              <div className="p-6">
                <h3 className="font-headline font-bold text-xl text-primary mb-1">
                  {executive.name}
                </h3>
                <p className="font-body text-sm text-accent mb-4">
                  {executive.title}
                </p>
                
                <div className="space-y-3 mb-4">
                  {executive.credentials.map((credential, credIndex) =>
                <div key={credIndex} className="flex items-start space-x-2">
                      <Icon
                    name="CheckCircleIcon"
                    size={16}
                    variant="solid"
                    className="text-success flex-shrink-0 mt-1" />

                      <span className="font-body text-sm text-text-secondary">
                        {credential}
                      </span>
                    </div>
                )}
                </div>
                
                <div className="pt-4 border-t border-border">
                  <p className="font-accent text-xs text-text-secondary uppercase tracking-wider mb-2">
                    Core Expertise
                  </p>
                  <p className="font-body text-sm text-text-primary">
                    {executive.expertise}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 p-8 bg-primary/5 border border-primary/10 rounded-sm">
          <div className="flex items-start space-x-4">
            <Icon
              name="AcademicCapIcon"
              size={28}
              variant="outline"
              className="text-primary flex-shrink-0 mt-1" />

            <div>
              <h4 className="font-headline font-semibold text-xl text-primary mb-3">
                Industry Experience That Matters
              </h4>
              <p className="font-body text-base text-text-secondary leading-relaxed mb-4">
                Our leadership team has collectively managed over $2 billion in advertising infrastructure, navigated compliance challenges across 40+ jurisdictions, and built white-label platforms serving Fortune 500 clients.
              </p>
              <p className="font-body text-base text-text-secondary leading-relaxed">
                We don't just understand advertising technology - we've architected the systems that power professional advertising at institutional scale.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default ExecutiveTeamSection;